/**
 * Keeping a customer conversation one conversation.
 *
 * Four small rules, and every one of them is invisible in code review and
 * obvious in somebody's inbox when it is wrong: a reply that starts a second
 * thread, a subject line that reads `Re: Re: Re: Your roof`, a message filed as
 * though the customer sent it when the rep did, and a reply that displays with
 * the entire previous conversation quoted underneath it.
 *
 * The Edge Function carries its own copy of these — it cannot import the
 * workspace — and `supabase/test/email-thread.test.ts` fails if the two
 * ever disagree. See ADR 0041.
 */

/** What a stored message needs to carry for the next reply to thread onto it. */
export interface ThreadedMessage {
  /** The `Message-ID:` header of that message, angle brackets and all. */
  rfc822MessageId: string | null;
  /** Its `References:` header, if it had one. */
  references?: string | null;
}

/** The headers a reply must carry. Empty strings are never emitted. */
export interface ThreadingHeaders {
  inReplyTo?: string;
  references?: string;
}

/**
 * `In-Reply-To` and `References` for a reply to `previous`.
 *
 * Mail clients thread on these, not on the subject. Gmail is forgiving enough
 * that a reply without them often still looks right in Gmail, which is exactly
 * what makes this easy to get wrong — the customer on Outlook or Apple Mail
 * gets a brand new conversation, and nobody in this office ever sees that
 * happen.
 *
 * `References` accumulates: it is the whole chain so far plus the message being
 * answered. A client that never sees the middle of a conversation uses it to
 * put the pieces back in order.
 */
export function threadingHeaders(
  previous: ThreadedMessage | null | undefined,
): ThreadingHeaders {
  const parent = previous?.rfc822MessageId?.trim();
  if (!parent) return {};

  const chain = (previous?.references ?? "")
    .split(/\s+/)
    .map((id) => id.trim())
    .filter(Boolean);

  // Guard against a malformed chain that already names the parent: repeating it
  // is legal but makes the header grow without bound over a long conversation.
  if (chain[chain.length - 1] !== parent) chain.push(parent);

  return { inReplyTo: parent, references: chain.join(" ") };
}

/** `Re:` at the front, exactly once, however many times it has been round. */
export function replySubject(subject: string | null | undefined): string {
  const trimmed = (subject ?? "").trim();
  if (!trimmed) return "Re:";
  // Matches `Re:`, `RE:`, `Re :` and Gmail's `Re[2]:`, and only at the front —
  // a subject that merely mentions the word is left alone.
  return /^re\s*(\[\d+\])?\s*:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

/**
 * Ours or theirs.
 *
 * Decided by comparing the sender against the mailbox the thread was fetched
 * from, because that is the only thing that is true regardless of who is
 * reading. A rep who replies from their phone rather than from Roof Board still
 * produces an outbound message, and it must not appear in the customer's
 * history as something the customer said.
 */
export function messageDirection(
  fromAddress: string | null | undefined,
  mailboxAddress: string | null | undefined,
): "inbound" | "outbound" {
  const from = (fromAddress ?? "").trim().toLowerCase();
  const mailbox = (mailboxAddress ?? "").trim().toLowerCase();
  if (!from || !mailbox) return "inbound";
  return from === mailbox ? "outbound" : "inbound";
}

/**
 * The part of a reply the customer actually typed.
 *
 * Mail clients quote the entire previous conversation underneath a reply. Left
 * in, a thread in Roof Board becomes unreadable by the third message — each one
 * containing every one before it — so the quoted tail is cut for display. The
 * full body is still stored; this is a reading aid, not a data change.
 *
 * The cut is deliberately conservative. Anything it fails to recognise is left
 * in the message, which is untidy; anything it cuts too eagerly is a customer's
 * words thrown away, which is not recoverable by reading the screen.
 */
export function stripQuotedReply(body: string | null | undefined): string {
  const text = (body ?? "").replace(/\r\n/g, "\n");
  if (!text.trim()) return "";

  const lines = text.split("\n");

  // The attribution line every major client writes above the quote. Gmail and
  // Apple Mail use "On <date> <someone> wrote:"; Outlook draws a rule and then
  // a block of From:/Sent:/To:. Both are matched at the start of a line only.
  const attribution =
    /^\s*(on\s.+\swrote:|-{2,}\s*original message\s*-{2,}|_{5,}|from:\s.+)$/i;

  let cut = lines.length;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    if (attribution.test(line)) {
      cut = i;
      break;
    }
    // A run of quoted lines that reaches the end of the message is quoted
    // history too, even without an attribution line above it.
    if (/^\s*>/.test(line)) {
      const restIsQuoted = lines
        .slice(i)
        .every((rest) => /^\s*>/.test(rest) || !rest.trim());
      if (restIsQuoted) {
        cut = i;
        break;
      }
    }
  }

  // A cut that would leave nothing means the guess was wrong — a reply that is
  // entirely quotation is far more likely to be a message this rule misread
  // than a customer who sent no words at all. Keep the original.
  const kept = lines.slice(0, cut).join("\n").trim();
  return kept || text.trim();
}
